import axios from "axios";
import Swal from "sweetalert2";
import { refreshSession } from "../auth/refreshManager";
import { postSessionMessage } from "../auth/sessionChannel";
import {
  SESSION_EXPIRED_MESSAGE,
  SESSION_EXPIRED_MESSAGE_KEY,
} from "../auth/performSignOut";
import tokenStore from "../auth/tokenStore";

const API_URL = process.env.REACT_APP_API_LOGIN_URL;

const axiosInstance = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

const newCorrelationId = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

axiosInstance.interceptors.request.use(
  (config) => {
    if (config.data && config.data instanceof FormData) {
      config.headers["Content-Type"] = "multipart/form-data";
    } else {
      config.headers["Content-Type"] = "application/json";
    }
    // Correlation ID: threaded to the gateway (and on to auth-service for the
    // refresh call) so one request is traceable end-to-end across all layers.
    // Reuse an id already set on a retried request so a refresh + replay share it.
    if (!config.headers["x-correlation-id"]) {
      config.headers["x-correlation-id"] = newCorrelationId();
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Non-critical telemetry/analytics calls must never force a logout.
// (e.g. login-activity logging, FCM token capture — their auth checks
// on the gateway can 401 independently of the user's real session.)
const NON_CRITICAL_PATHS = [
  "/misc/createLoginActivity",
  "/notification/captureUserFirebaseToken",
];
const isNonCritical = (request: any) =>
  NON_CRITICAL_PATHS.some((p) => request?.url?.includes(p));

// Endpoints that ESTABLISH a session rather than consume one. A 401 here means
// the credentials were rejected ("Invalid credentials", "User is inactive", ...)
// — there is no session to refresh, so running the refresh path would fail and
// report a plain credential error to the user as "Session Expired". Reject
// straight through so the caller can surface the server's own message.
const SESSION_ESTABLISHING_PATHS = [
  "/auth/login",
  "/auth/refresh",
  "/auth/sso/login",
  "/auth/session/handoff",
];
const isSessionEstablishing = (request: any) =>
  SESSION_ESTABLISHING_PATHS.some((p) => request?.url?.includes(p));

// Refresh failed (refresh token invalid/expired/revoked) → the session is
// really over: tell the other tabs, explain, clear everything, go to login.
const forceReLogin = async () => {
  postSessionMessage({ type: "LOGOUT", reason: "expired" });
  Swal.fire({
    title: "Session Expired",
    text: SESSION_EXPIRED_MESSAGE,
    icon: "warning",
    confirmButtonColor: "#3085d6",
    confirmButtonText: "Ok",
  }).then((result) => {
    if (result.isConfirmed) {
      window.location.href = "/";
    }
  });
  await new Promise((resolve) => setTimeout(resolve, 10000));
  tokenStore.clearAll();
  localStorage.clear();
  sessionStorage.clear();
  if ("caches" in window) {
    const keyList = await caches.keys();
    await Promise.all(keyList.map((key) => caches.delete(key)));
  }
  if (window.indexedDB && indexedDB.databases) {
    const databases = await indexedDB.databases();
    databases.forEach((db: any) => db?.name && indexedDB.deleteDatabase(db.name));
  }
  try {
    sessionStorage.setItem(SESSION_EXPIRED_MESSAGE_KEY, SESSION_EXPIRED_MESSAGE);
  } catch {}
  window.location.href = "/";
  window.location.reload();
};

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    // For responseType:"blob" requests (file downloads) an error body also
    // arrives as a Blob, which would hide the status handling below. Parse
    // JSON-typed error blobs back into objects first.
    if (
      error?.response?.data instanceof Blob &&
      error.response.data.type?.includes("json")
    ) {
      try {
        error.response.data = JSON.parse(await error.response.data.text());
      } catch (parseError) {
        // leave the blob as-is; downstream handling falls back to defaults
      }
    }
    if (!navigator.onLine) {
      await new Promise((resolve) => setTimeout(resolve, 15000));
      return axiosInstance(originalRequest);
    }
    // Never run the refresh path for the refresh call itself (recursion) or for
    // any other call that is trying to CREATE the session in the first place.
    if (!originalRequest || isSessionEstablishing(originalRequest)) {
      return Promise.reject(error);
    }

    // Treat ANY 401 as a possibly-expired session — not just the exact
    // "Token Expired" message. Only ~10 of the gateway's ~2000 endpoints call
    // validateCookie(cookies, true) and emit "Token Expired"; the rest resolve
    // the user from Redis and throw 401 "Unauthorized" once the access token's
    // Redis entry (TTL = access-token life) expires. Keying off the message
    // alone missed ~99% of endpoints and redirected to login instead of
    // refreshing. The _retry guard below still caps this at one refresh, so a
    // genuine authorization failure refreshes once, retries, then falls through
    // to the redirect branch.
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        // Single-flight: concurrent 401s all await the SAME refresh inside
        // refreshManager (the backend rotates the refresh token, so a second
        // parallel refresh call would be sent with a stale token and fail).
        const authHeader = await refreshSession();
        originalRequest.headers["Authorization"] = authHeader;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        if (isNonCritical(originalRequest)) {
          console.warn("Non-critical 401 ignored:", originalRequest?.url);
          return error?.response?.data;
        }
        await forceReLogin();
        return Promise.reject(refreshError);
      }
    }

    if (error.response?.status === 401 && window.location.pathname !== "/") {
      // Reached only after a refresh was already attempted (_retry) — a
      // genuine auth failure — so redirect to login.
      if (isNonCritical(originalRequest)) {
        console.warn("Non-critical 401 ignored:", originalRequest?.url);
        return error?.response?.data;
      }
      console.log("Unauthorized access");
      window.location.href = "/";
      return error?.response?.data;
    } else if (error.response?.status === 409) {
      return error.response;
    } else if (error.response?.status === 400) {
      if (error?.response?.data?.message === "No cookies found.") {
        window.location.href = "/";
      }
      return error?.response;
    } else if (error.response?.status === 404) {
      return error.response;
    }
    console.log("------ Error ---------", error?.response?.data);
    throw error?.response?.data;
  }
);

export default axiosInstance;
